/**
 * Master Apps · Usuários — a tela de CONFERÊNCIA do vínculo com o RH (admin).
 *
 * A pergunta que ela responde não é "quem tem conta", é "o acesso de cada conta
 * ainda corresponde ao que o RH diz". Por isso cada linha mostra, lado a lado, o
 * RETRATO gravado na conta (matrícula, função, setor, filial — escrito UMA vez,
 * no onboarding, e é ELE que o filtro de acesso usa) e o que o RH diz HOJE.
 *
 * Toda regra chega PRONTA da rota (`semMatricula`, `semEspelhoRh`, `divergente`,
 * `divergencias`): aqui não se recalcula divergência nem se decide quem pode
 * entrar — a tela pinta o veredito do servidor. É o que impede a definição de
 * "divergente" de existir em dois lugares e divergir de si mesma.
 */
import { type CSSProperties, type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { IconSearch } from '@/components/icons';
import { Sidebar, TopBar } from '@/components/primitives';
import { SelectMenu } from '@/components/select-menu';
import {
  type AdminInviteRow,
  type UserRow,
  useAdminInvites,
  useCreateAdminInvite,
  useRevokeAdminInvite,
  useUpdateUser,
} from '@/hooks/use-users';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import { formatDateBR, normalizeSearchText } from '@/lib/format';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  numeroTabular,
  rotuloCaixaAlta,
  type ScreenProps,
} from '@/lib/theme';

const DANGER = '#dc2626';
/** Âmbar de ATENÇÃO: divergência e conta sem matrícula não bloqueiam, mas doem. */
const WARN = '#ca8a04';
const OK = '#16a34a';

/**
 * Grade da tabela. Oito colunas porque são oito perguntas da auditoria: quem é,
 * qual matrícula, qual função, qual setor, qual filial, qual papel, qual
 * situação e o que dá para fazer a respeito.
 */
const GRID_COLUMNS = '2.5fr 110px 1.2fr 1.5fr 1.3fr 92px 1.3fr 176px';

/**
 * Piso de largura da linha quando a grade rola de lado (tablet). As colunas
 * fixas somam 378px e os 7 vãos de 14px somam 98px: 476px que não encolhem. Com
 * 1240px de linha sobram 764px de espaço fracionário (7,8fr → ~98px por fr), o
 * que dá ~245px para a coluna de identificação — 203px de texto depois do avatar
 * de 32px e do vão de 10px, acima dos ~140px que "PAULO ARRUDA GOMES" pede a
 * 13px. Abaixo disso o nome e o setor viram reticências e a tabela deixa de
 * responder à pergunta que ela existe para responder.
 *
 * A repartição das frações foi medida no navegador a 1440px (onde a linha tem
 * 1172px, descontada a barra de 220px e a folga de 24px): identificação em 2,5fr
 * e FUNÇÃO em 1,2fr, e não o contrário. Função é o campo menos decisivo da linha
 * — não conta nem para divergência —, enquanto nome truncado (“JOSINEIDE
 * GUERR…”) obriga o admin a passar o mouse para saber de quem é a conta.
 */
const TABLE_MIN_WIDTH = 1240;

/**
 * Rolagem lateral DELIBERADA no tablet: é isto ou comprimir oito colunas em
 * 768px até o conteúdo desaparecer. `scrollbarWidth: thin` para a barra não
 * roubar altura das linhas.
 */
const TABLE_SCROLLER: CSSProperties = { overflowX: 'auto', scrollbarWidth: 'thin' };

/**
 * Situações do RH (CODSITUACAO) com rótulo CONHECIDO. As outras ('P', 'E', 'I',
 * 'T', 'V', 'Q') aparecem como código puro de propósito: o significado exato
 * delas não está documentado no projeto, e inventar rótulo numa tela de
 * auditoria é pior do que mostrar o código que o admin vai citar ao ligar para o
 * RH.
 *
 * REGRA DE ACESSO (decisão de produto, não bug): só 'A' e 'F' dão acesso —
 * FÉRIAS MANTÉM O ACESSO. Quem desativa é a conciliação; esta tela só explica.
 */
const SITUACAO_RH: Record<string, string> = {
  A: 'Ativo',
  F: 'Férias',
  D: 'Demitido',
};

/** Situação do RH em texto: "Férias (F)" quando conhecida, "I" quando não. */
function situacaoTexto(situacao: string | null): string {
  if (!situacao) return 'não conferida';
  const rotulo = SITUACAO_RH[situacao];
  if (!rotulo) return `situação "${situacao}"`;
  return `${rotulo} (${situacao})`;
}

/**
 * Códigos ESTÁVEIS que a conciliação grava em `deactivationReason`. Qualquer
 * outro valor é texto livre de um admin — e é essa a distinção que a tela precisa
 * deixar clara: desativação pelo RH é consequência, desativação manual é decisão
 * de uma pessoa (e a conversa com o funcionário é outra).
 */
const MOTIVOS_CONCILIACAO: Record<string, string> = {
  demitido_rh: 'Demitido no RH',
  situacao_inativa: 'Situação sem acesso no RH',
  sem_matricula: 'Sem matrícula vinculada',
};

function motivoDesativacao(u: UserRow): string {
  const codigo = u.deactivationReason;
  if (!codigo) return 'motivo não registrado';
  const conciliacao = MOTIVOS_CONCILIACAO[codigo];
  // Texto livre = desativação manual. Vai prefixado para ninguém confundir com
  // uma decisão do RH.
  if (!conciliacao) return `manual · ${codigo}`;
  if (codigo === 'situacao_inativa') return `${conciliacao} · ${situacaoTexto(u.rhSituacao)}`;
  return conciliacao;
}

function nomeExibido(u: UserRow): string {
  // Ordem de fallback: nome do Clerk → nome do RH → parte local do e-mail.
  // Nunca "—": a linha inteira fala de uma PESSOA, ela precisa de um nome.
  return u.nome ?? u.rh?.nome ?? u.email.split('@')[0] ?? u.email;
}

function iniciais(u: UserRow): string {
  const partes = nomeExibido(u).trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes.at(-1)?.[0] ?? '') : '';
  return `${primeira}${ultima}`.toUpperCase() || 'U';
}

// ── Peças visuais ────────────────────────────────────────────────────────────

/** Foto do Clerk quando existe; iniciais quando não (o espelho pode não ter). */
function UserAvatar({
  u,
  accent,
  size,
  dim,
}: Readonly<{ u: UserRow; accent: string; size: number; dim: boolean }>) {
  const base: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    flexShrink: 0,
    // Conta desativada aparece esmaecida: o estado tem de ser legível de longe,
    // antes de qualquer texto ser lido.
    opacity: dim ? 0.45 : 1,
  };
  if (u.imageUrl) {
    return <img src={u.imageUrl} alt="" style={{ ...base, objectFit: 'cover' }} />;
  }
  return (
    <div
      style={{
        ...base,
        background: accent,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size < 36 ? 12 : 14,
        fontWeight: 700,
      }}
    >
      {iniciais(u)}
    </div>
  );
}

/** Etiqueta de estado — uma cor por tom, sem depender só de texto. */
function Badge({
  children,
  color,
  dark,
}: Readonly<{ children: ReactNode; color: string; dark: boolean }>) {
  return (
    <span
      style={{
        ...rotuloCaixaAlta,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 7px',
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        background: dark ? `${color}22` : `${color}1a`,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  approved: { label: 'Ativo', color: OK },
  inactive: { label: 'Desativado', color: DANGER },
  pending: { label: 'Pendente', color: WARN },
  rejected: { label: 'Rejeitado', color: DANGER },
};

function StatusCell({ u, dark }: Readonly<{ u: UserRow; dark: boolean }>) {
  const p = makePalette(dark);
  const cfg = STATUS_BADGE[u.status] ?? { label: u.status, color: p.muted };
  return (
    <div
      style={{ minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Badge color={cfg.color} dark={dark}>
        {cfg.label}
      </Badge>
      {u.status === 'inactive' && (
        <span style={{ fontSize: 10.5, color: DANGER, overflowWrap: 'anywhere' }}>
          {motivoDesativacao(u)}
          {u.deactivatedAt ? ` · ${formatDateBR(u.deactivatedAt)}` : ''}
        </span>
      )}
      {u.status === 'approved' && u.rhSituacao && (
        <span style={{ fontSize: 10.5, color: p.muted, fontFamily: fontSans }}>
          RH: {situacaoTexto(u.rhSituacao)}
        </span>
      )}
    </div>
  );
}

function RoleBadge({
  role,
  dark,
  accent,
}: Readonly<{ role: string; dark: boolean; accent: string }>) {
  const p = makePalette(dark);
  if (role === 'admin') {
    return (
      <Badge color={accent} dark={dark}>
        Admin
      </Badge>
    );
  }
  return (
    <Badge color={p.muted} dark={dark}>
      Usuário
    </Badge>
  );
}

/**
 * Célula "retrato × RH": em cima o valor GRAVADO na conta (o que decide acesso),
 * embaixo, só quando divergem, o que o RH diz hoje. Duas linhas em vez de uma
 * seta: numa tabela densa a seta some, e o admin precisa ler os DOIS valores para
 * saber para onde a pessoa foi transferida.
 */
function RetratoVsRh({
  retrato,
  rh,
  divergente,
  dark,
}: Readonly<{ retrato: string | null; rh: string | null; divergente: boolean; dark: boolean }>) {
  const p = makePalette(dark);
  const valor = retrato?.trim() || '—';
  return (
    <div style={{ minWidth: 0, overflow: 'hidden' }}>
      {/* `numeroTabular` mesmo a célula servindo dois papéis: com FILIAL o valor
          é o código de dois dígitos ('01', '05') e a figura tabular alinha a
          coluna; com SETOR é texto, onde `tabular-nums` é no-op. Nunca foi mono
          aqui — a figura tabular é o que faltava. */}
      <div
        style={{
          ...numeroTabular,
          fontSize: 12,
          color: divergente ? WARN : p.textMuted,
          fontWeight: divergente ? 600 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={valor}
      >
        {valor}
      </div>
      {divergente && (
        <div
          style={{
            fontSize: 10.5,
            color: p.muted,
            fontFamily: fontSans,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={`RH hoje: ${rh ?? '—'}`}
        >
          RH: {rh?.trim() || '—'}
        </div>
      )}
    </div>
  );
}

/** Matrícula — o dado que o usuário mais precisa conferir. */
function MatriculaCell({ u, dark }: Readonly<{ u: UserRow; dark: boolean }>) {
  const p = makePalette(dark);
  if (u.semMatricula) {
    return (
      <Badge color={WARN} dark={dark}>
        Sem matrícula
      </Badge>
    );
  }
  return (
    <div style={{ minWidth: 0, overflow: 'hidden' }}>
      <div
        style={{
          fontSize: 12.5,
          fontFamily: fontMono,
          fontVariantNumeric: 'tabular-nums',
          color: p.text,
        }}
      >
        {u.matricula}
      </div>
      {u.semEspelhoRh && (
        <Badge color={DANGER} dark={dark}>
          Fora do RH
        </Badge>
      )}
    </div>
  );
}

/** Botão de ação por linha. 40px de alvo no toque, 28 no desktop denso. */
function RowAction({
  children,
  dark,
  danger = false,
  disabled = false,
  title,
  height,
  grow = false,
  onClick,
}: Readonly<{
  children: ReactNode;
  dark: boolean;
  danger?: boolean;
  disabled?: boolean;
  title: string;
  height: number;
  grow?: boolean;
  onClick: () => void;
}>) {
  const p = makePalette(dark);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        height,
        flex: grow ? 1 : undefined,
        padding: '0 10px',
        background: 'transparent',
        border: `1px solid ${danger ? DANGER : p.border}`,
        borderRadius: 6,
        fontSize: 11.5,
        fontWeight: 600,
        fontFamily: fontSans,
        color: danger ? DANGER : p.textMuted,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

interface RowProps {
  u: UserRow;
  dark: boolean;
  accent: string;
  /** Uma escrita em andamento nesta linha: os botões não podem disparar duas vezes. */
  busy: boolean;
  divider: boolean;
  actionHeight: number;
  onToggleRole: () => void;
}

/**
 * Única ação por conta: papel. Ativar/desativar NÃO é ação de tela — quem
 * decide é o RH, via conciliação do sync (demitido/fora da origem desativa e a
 * conta some desta lista; matrícula que volta a 'A'/'F' reativa sozinha).
 */
function RowActions({
  u,
  dark,
  busy,
  actionHeight,
  grow,
  onToggleRole,
}: Readonly<Omit<RowProps, 'accent' | 'divider'> & { grow: boolean }>) {
  const nome = nomeExibido(u);
  const virarAdmin = u.role !== 'admin';
  return (
    <RowAction
      dark={dark}
      disabled={busy}
      grow={grow}
      height={actionHeight}
      title={virarAdmin ? `Tornar ${nome} administrador` : `Remover admin de ${nome}`}
      onClick={onToggleRole}
    >
      {virarAdmin ? 'Tornar admin' : 'Remover admin'}
    </RowAction>
  );
}

/**
 * Faixa colorida na borda esquerda da linha/card. É o realce que faz o estado
 * saltar sem depender de ler texto: vermelho = desativado, âmbar = precisa de
 * atenção (sem matrícula, matrícula fora do RH, retrato divergente).
 */
function realceDe(u: UserRow): string | null {
  if (u.status === 'inactive') return DANGER;
  if (u.semMatricula || u.semEspelhoRh || u.divergente) return WARN;
  return null;
}

/** Uma linha da grade (tablet e desktop). */
function UserGridRow({
  u,
  dark,
  accent,
  busy,
  divider,
  actionHeight,
  onToggleRole,
}: Readonly<RowProps>) {
  const p = makePalette(dark);
  const realce = realceDe(u);
  const divergeSetor = u.divergencias.includes('setor');
  const divergeFilial = u.divergencias.includes('filial');
  return (
    // A linha NÃO é clicável: não existe tela de detalhe de usuário, e sem
    // destino não há por que fingir um alvo de clique (nem cair no `div
    // role="button"` que a regra S6819 proíbe). As ações são botões de verdade.
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        gap: 14,
        padding: '12px 16px',
        alignItems: 'center',
        borderBottom: divider ? `1px solid ${p.border}` : 'none',
        borderLeft: `3px solid ${realce ?? 'transparent'}`,
        background: u.status === 'inactive' ? `${DANGER}0d` : 'transparent',
        fontSize: 12.5,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <UserAvatar u={u} accent={accent} size={32} dim={u.status === 'inactive'} />
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: p.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={nomeExibido(u)}
          >
            {nomeExibido(u)}
          </div>
          <div
            style={{
              fontSize: 11,
              color: p.muted,
              fontFamily: fontMono,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={u.email}
          >
            {u.email}
          </div>
        </div>
      </div>
      <MatriculaCell u={u} dark={dark} />
      <RetratoVsRh retrato={u.funcao} rh={u.rh?.funcao ?? null} divergente={false} dark={dark} />
      <RetratoVsRh
        retrato={u.setorNome}
        rh={u.rh?.setorNome ?? null}
        divergente={divergeSetor}
        dark={dark}
      />
      <RetratoVsRh
        retrato={u.totvsFilialCode}
        rh={u.rh?.codFilial ?? null}
        divergente={divergeFilial}
        dark={dark}
      />
      <div>
        <RoleBadge role={u.role} dark={dark} accent={accent} />
      </div>
      <StatusCell u={u} dark={dark} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
        <RowActions
          u={u}
          dark={dark}
          busy={busy}
          grow={false}
          actionHeight={actionHeight}
          onToggleRole={onToggleRole}
        />
      </div>
    </div>
  );
}

/**
 * Um atributo dentro do card: RÓTULO à esquerda, valor à direita. O rótulo é o
 * que substitui o cabeçalho de coluna — "2" e "SOLUÇÕES DIGITAIS" soltos num
 * card não dizem de que campo são.
 */
function CardField({
  label,
  children,
  dark,
}: Readonly<{ label: string; children: ReactNode; dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 10,
        minWidth: 0,
      }}
    >
      <div
        style={{
          ...rotuloCaixaAlta,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: p.muted,
          flexShrink: 0,
          paddingTop: 2,
        }}
      >
        {label}
      </div>
      {/* `overflowWrap: anywhere` e não reticências: no card há largura para
          embrulhar, e cortar o setor ou o e-mail é perder o dado. */}
      <div
        style={{
          minWidth: 0,
          textAlign: 'right',
          overflowWrap: 'anywhere',
          color: p.text,
          fontSize: 12.5,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Valor do card com o "RH hoje" embaixo quando há divergência. */
function CardRetrato({
  retrato,
  rh,
  divergente,
  dark,
}: Readonly<{ retrato: string | null; rh: string | null; divergente: boolean; dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <>
      {/* Mesma intenção do `RetratoVsRh` do desktop: com FILIAL o valor é código
          numérico, então figura tabular; com SETOR/FUNÇÃO é texto e é no-op. */}
      <div
        style={{
          ...numeroTabular,
          color: divergente ? WARN : p.text,
          fontWeight: divergente ? 600 : 400,
        }}
      >
        {retrato?.trim() || '—'}
      </div>
      {divergente && (
        <div style={{ fontSize: 11, color: p.muted, fontFamily: fontSans }}>
          RH: {rh?.trim() || '—'}
        </div>
      )}
    </>
  );
}

/**
 * O mesmo usuário em 375px. Oito colunas dariam 47px cada, então a linha vira
 * card com um atributo por linha. As duas ações ficam numa faixa própria, com
 * 40px de altura (alvo de toque) e dividindo a largura.
 */
function UserCard({
  u,
  dark,
  accent,
  busy,
  actionHeight,
  onToggleRole,
}: Readonly<Omit<RowProps, 'divider'>>) {
  const p = makePalette(dark);
  const realce = realceDe(u);
  return (
    <div
      style={{
        background: u.status === 'inactive' ? `${DANGER}0d` : p.surface,
        border: `1px solid ${p.border}`,
        borderLeft: `3px solid ${realce ?? p.border}`,
        borderRadius: 10,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
        <UserAvatar u={u} accent={accent} size={40} dim={u.status === 'inactive'} />
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: p.text, overflowWrap: 'anywhere' }}>
            {nomeExibido(u)}
          </span>
          <span
            style={{
              fontSize: 11.5,
              color: p.muted,
              fontFamily: fontMono,
              overflowWrap: 'anywhere',
            }}
          >
            {u.email}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <RoleBadge role={u.role} dark={dark} accent={accent} />
        <StatusCell u={u} dark={dark} />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          paddingTop: 8,
          borderTop: `1px solid ${p.border}`,
        }}
      >
        <CardField label="Matrícula" dark={dark}>
          <MatriculaCell u={u} dark={dark} />
        </CardField>
        <CardField label="Função" dark={dark}>
          <CardRetrato retrato={u.funcao} rh={null} divergente={false} dark={dark} />
        </CardField>
        <CardField label="Setor" dark={dark}>
          <CardRetrato
            retrato={u.setorNome}
            rh={u.rh?.setorNome ?? null}
            divergente={u.divergencias.includes('setor')}
            dark={dark}
          />
        </CardField>
        <CardField label="Filial" dark={dark}>
          <CardRetrato
            retrato={u.totvsFilialCode}
            rh={u.rh?.codFilial ?? null}
            divergente={u.divergencias.includes('filial')}
            dark={dark}
          />
          {u.rh?.filial && <div style={{ fontSize: 11, color: p.muted }}>{u.rh.filial}</div>}
        </CardField>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <RowActions
          u={u}
          dark={dark}
          busy={busy}
          grow
          actionHeight={actionHeight}
          onToggleRole={onToggleRole}
        />
      </div>
    </div>
  );
}

// ── Filtros e contadores ─────────────────────────────────────────────────────

type Filtro = 'todos' | 'admins' | 'ativos' | 'sem-matricula' | 'divergentes';

/**
 * Chip de contador/filtro local em vez do `FilterChip` de `primitives.tsx`: o
 * primitivo tem altura FIXA de 30px, abaixo do piso de 40px de alvo de toque, e
 * esta tela é usada no celular. Mesmo desenho, altura por faixa de viewport.
 */
function CountChip({
  label,
  count,
  active,
  dark,
  height,
  onClick,
}: Readonly<{
  label: string;
  count: number;
  active: boolean;
  dark: boolean;
  height: number;
  onClick: () => void;
}>) {
  const p = makePalette(dark);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height,
        padding: '0 12px',
        background: active ? p.text : 'transparent',
        color: active ? p.surface : p.textMuted,
        border: `1px solid ${active ? p.text : p.border}`,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: fontSans,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      <span
        style={{
          ...numeroTabular,
          fontSize: 10,
          fontWeight: 700,
          opacity: active ? 0.7 : 0.55,
        }}
      >
        {count}
      </span>
    </button>
  );
}

interface Contadores {
  todos: number;
  admins: number;
  ativos: number;
  semMatricula: number;
  divergentes: number;
}

/**
 * "1 conta" / "6 contas". Com um único usuário no banco (o caso REAL do
 * ambiente hoje) o subtítulo dizia "1 contas · 1 divergentes" — plural errado no
 * cabeçalho é o tipo de detalhe que faz o usuário duvidar do resto do número.
 */
const contagem = (n: number, singular: string, plural: string): string =>
  `${n} ${n === 1 ? singular : plural}`;

function contar(users: UserRow[]): Contadores {
  return {
    todos: users.length,
    admins: users.filter((u) => u.role === 'admin').length,
    // "Ativos" é `status === 'approved'`: pending e rejected (marcação manual
    // no banco) não entram aqui. Desativados nem chegam — o GET /api/users os
    // exclui (a decisão é do RH, via conciliação do sync).
    ativos: users.filter((u) => u.status === 'approved').length,
    semMatricula: users.filter((u) => u.semMatricula).length,
    divergentes: users.filter((u) => u.divergente).length,
  };
}

function passaNoFiltro(u: UserRow, filtro: Filtro): boolean {
  switch (filtro) {
    case 'admins':
      return u.role === 'admin';
    case 'ativos':
      return u.status === 'approved';
    case 'sem-matricula':
      return u.semMatricula;
    case 'divergentes':
      return u.divergente;
    default:
      return true;
  }
}

/**
 * Busca por nome, e-mail e MATRÍCULA, sem acento (`normalizeSearchText`): a base
 * do RH é cheia de acento e ninguém digita "SOLUÇÕES". O nome do RH também entra,
 * porque a conta pode nunca ter recebido nome do Clerk.
 */
function passaNaBusca(u: UserRow, termo: string): boolean {
  if (!termo) return true;
  const campos = [u.nome ?? '', u.rh?.nome ?? '', u.email, u.matricula ?? ''];
  return campos.some((campo) => normalizeSearchText(campo).includes(termo));
}

// ── Tela ─────────────────────────────────────────────────────────────────────

/**
 * ── Pré-autorização de administradores (`admin_invites`) ─────────────────────
 * Conta de usuário só nasce no primeiro login SSO, então "adicionar um
 * administrador" que nunca entrou é registrar o e-mail aqui: quando a conta for
 * criada com ele, já nasce admin (a regra vive no provisionamento da API). O
 * convite consumido permanece como trilha de auditoria — quem autorizou × quem
 * herdou — e por isso não tem botão de revogar; para conta existente o caminho
 * é o "Tornar admin" da própria lista.
 */
function AdminInvitesPanel({
  dark,
  accent,
  controlHeight,
}: Readonly<{ dark: boolean; accent: string; controlHeight: number }>) {
  const p = makePalette(dark);
  const invites = useAdminInvites();
  const criar = useCreateAdminInvite();
  const revogar = useRevokeAdminInvite();
  const [email, setEmail] = useState('');

  const enviar = () => {
    const alvo = email.trim();
    if (!alvo || criar.isPending) return;
    criar.mutate(alvo, {
      onSuccess: (c) => {
        toast.success(`${c.email} pré-autorizado — a conta nasce administradora no primeiro login`);
        setEmail('');
      },
      // 409 da API explica o que fazer (já autorizado; conta existente →
      // "Tornar admin" na lista) — a mensagem do envelope é a da tela.
      onError: (e: Error) => toast.error(e.message),
    });
  };

  const revogarConvite = (i: AdminInviteRow) => {
    if (revogar.isPending) return;
    const texto = `Revogar a pré-autorização de ${i.email}?\n\nSe a pessoa criar a conta depois disso, ela nasce como colaborador comum.`;
    if (!window.confirm(texto)) return;
    revogar.mutate(i.id, {
      onSuccess: () => toast.success('Pré-autorização revogada'),
      onError: (e: Error) => toast.error(e.message),
    });
  };

  const lista = invites.data ?? [];

  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 10,
        padding: 14,
        marginBottom: 12,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: p.text, marginBottom: 2 }}>
        Administradores pré-autorizados
      </div>
      <div style={{ fontSize: 11.5, color: p.muted, lineHeight: 1.5, marginBottom: 10 }}>
        Quem ainda não tem conta entra aqui: no primeiro login com o e-mail abaixo, a conta já nasce
        administradora. Para quem já tem conta, use o “Tornar admin” da lista.
      </div>

      <form
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          enviar();
        }}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: lista.length ? 12 : 0 }}
      >
        <input
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          placeholder="email@masterboi.com.br"
          aria-label="E-mail a pré-autorizar como administrador"
          type="email"
          style={{
            flex: '1 1 240px',
            minWidth: 0,
            height: controlHeight + 14,
            padding: '0 10px',
            background: p.surface2,
            border: `1px solid ${p.border}`,
            borderRadius: 6,
            outline: 'none',
            fontSize: 12,
            color: p.text,
            fontFamily: fontMono,
          }}
        />
        <button
          type="submit"
          disabled={criar.isPending || email.trim() === ''}
          style={{
            height: controlHeight + 14,
            padding: '0 14px',
            background: accent,
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            fontFamily: fontSans,
            cursor: criar.isPending ? 'progress' : 'pointer',
            opacity: criar.isPending || email.trim() === '' ? 0.6 : 1,
            flexShrink: 0,
          }}
        >
          {criar.isPending ? 'Adicionando…' : 'Pré-autorizar'}
        </button>
      </form>

      {invites.isLoading && (
        <div style={{ fontSize: 11.5, color: p.muted }}>Carregando pré-autorizações…</div>
      )}
      {lista.map((i, idx) => (
        <div
          key={i.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            padding: '8px 0',
            borderTop: idx === 0 ? `1px solid ${p.border}` : undefined,
            borderBottom: `1px solid ${p.border}`,
            marginBottom: -1,
          }}
        >
          <span style={{ fontSize: 12, fontFamily: fontMono, color: p.text, whiteSpace: 'nowrap' }}>
            {i.email}
          </span>
          <span style={{ fontSize: 10.5, color: p.muted, whiteSpace: 'nowrap' }}>
            por {i.criadoPorEmail ?? '—'} · {formatDateBR(i.createdAt)}
          </span>
          <span style={{ flex: 1 }} />
          {i.consumedAt ? (
            <span
              style={{
                ...rotuloCaixaAlta,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: OK,
                whiteSpace: 'nowrap',
              }}
            >
              Admin desde {formatDateBR(i.consumedAt)}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => revogarConvite(i)}
              disabled={revogar.isPending}
              style={{
                height: 26,
                padding: '0 10px',
                background: 'transparent',
                border: `1px solid ${DANGER}55`,
                color: DANGER,
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                fontFamily: fontSans,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Revogar
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function ScreenUsers({
  dark,
  accent = NOC_RED,
  users,
}: Readonly<ScreenProps & { users: UserRow[] }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busca, setBusca] = useState('');
  // Paginação CLIENTE (a lista chega inteira da API): aplicada DEPOIS do filtro
  // e da busca, com a página voltando para 1 sempre que qualquer um deles muda
  // — senão um filtro que encolhe a lista deixava o usuário numa página vazia.
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);
  const update = useUpdateUser();

  const mudarFiltro = (f: Filtro) => {
    setFiltro(f);
    setPagina(1);
  };
  const mudarBusca = (v: string) => {
    setBusca(v);
    setPagina(1);
  };

  const totais = contar(users);
  const termo = normalizeSearchText(busca);
  const visiveis = users.filter((u) => passaNoFiltro(u, filtro) && passaNaBusca(u, termo));

  const totalPaginas = Math.max(1, Math.ceil(visiveis.length / porPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * porPagina;
  const paginados = visiveis.slice(inicio, inicio + porPagina);

  // Alvos de toque: 40px no celular e no tablet (o tablet é tela de dedo),
  // tamanho denso original no desktop.
  const chipHeight = byViewport(vp, { mobile: 40, tablet: 40, desktop: 30 });
  const actionHeight = byViewport(vp, { mobile: 40, tablet: 40, desktop: 28 });
  const emAndamento = update.isPending ? (update.variables?.id ?? null) : null;

  const aplicar = (id: string, input: Parameters<typeof update.mutate>[0]['input'], ok: string) => {
    update.mutate(
      { id, input },
      {
        onSuccess: () => toast.success(ok),
        // A mensagem vem do envelope da API: os dois 409 (lockout do próprio
        // admin e reativação que o RH não libera) explicam o que fazer.
        onError: (e: Error) => toast.error(e.message),
      },
    );
  };

  const alternarPapel = (u: UserRow) => {
    if (update.isPending) return;
    const nome = nomeExibido(u);
    const virarAdmin = u.role !== 'admin';
    const texto = virarAdmin
      ? `Tornar ${nome} administrador?\n\nAdministrador vê e edita o inventário inteiro, os usuários e o acesso das aplicações.`
      : `Remover o acesso de administrador de ${nome}?\n\nA conta continua ativa, mas passa a ver só as aplicações liberadas para ela.`;
    if (!window.confirm(texto)) return;
    aplicar(
      u.id,
      { role: virarAdmin ? 'admin' : 'user' },
      virarAdmin ? `${nome} agora é administrador` : `${nome} não é mais administrador`,
    );
  };

  const cabecalho = (
    <div
      style={{
        ...rotuloCaixaAlta,
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        gap: 14,
        padding: '12px 16px',
        borderBottom: `1px solid ${p.border}`,
        borderLeft: '3px solid transparent',
        background: p.surface2,
        fontSize: 10,
        fontWeight: 700,
        color: p.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}
    >
      <div>Usuário</div>
      <div>Matrícula</div>
      <div>Função</div>
      <div>Setor</div>
      <div>Filial</div>
      <div>Papel</div>
      <div>Situação</div>
      <div style={{ textAlign: 'right' }}>Ações</div>
    </div>
  );

  const vazio = visiveis.length === 0 && (
    <div style={{ padding: 32, textAlign: 'center', color: p.muted, fontSize: 13 }}>
      {users.length === 0
        ? 'Nenhum usuário ainda. Eles aparecem aqui depois do primeiro login via Microsoft.'
        : 'Nenhum usuário corresponde ao filtro e à busca atuais.'}
    </div>
  );

  const linhas = paginados.map((u, i) => (
    <UserGridRow
      key={u.id}
      u={u}
      dark={dark}
      accent={accent}
      busy={emAndamento === u.id}
      divider={i < paginados.length - 1}
      actionHeight={actionHeight}
      onToggleRole={() => alternarPapel(u)}
    />
  ));

  const cards = paginados.map((u) => (
    <UserCard
      key={u.id}
      u={u}
      dark={dark}
      accent={accent}
      busy={emAndamento === u.id}
      actionHeight={actionHeight}
      onToggleRole={() => alternarPapel(u)}
    />
  ));

  const grade = (
    <>
      {cabecalho}
      {vazio}
      {linhas}
    </>
  );

  // No celular o painel deixa de ser moldura (cada card tem a sua) e vira só a
  // pilha; no tablet e no desktop é a moldura arredondada de sempre.
  const moldura: CSSProperties = {
    background: p.surface,
    border: `1px solid ${p.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  };
  const painel = byViewport<CSSProperties>(vp, {
    mobile: { display: 'flex', flexDirection: 'column', gap: 10 },
    tablet: moldura,
    desktop: moldura,
  });

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="users" dark={dark} accent={accent} role="admin" />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          dark={dark}
          title="Usuários"
          subtitle={`${contagem(totais.todos, 'conta', 'contas')} · ${totais.admins} admin · ${contagem(totais.divergentes, 'divergente do RH', 'divergentes do RH')}`}
        />
        <div
          style={{
            flex: 1,
            // 24px de folga de cada lado em 320px de tela são 15% da largura útil.
            padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 24 }),
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 10,
            }}
          >
            <CountChip
              label="Todos"
              count={totais.todos}
              active={filtro === 'todos'}
              dark={dark}
              height={chipHeight}
              onClick={() => mudarFiltro('todos')}
            />
            <CountChip
              label="Admins"
              count={totais.admins}
              active={filtro === 'admins'}
              dark={dark}
              height={chipHeight}
              onClick={() => mudarFiltro('admins')}
            />
            <CountChip
              label="Ativos"
              count={totais.ativos}
              active={filtro === 'ativos'}
              dark={dark}
              height={chipHeight}
              onClick={() => mudarFiltro('ativos')}
            />
            <CountChip
              label="Sem matrícula"
              count={totais.semMatricula}
              active={filtro === 'sem-matricula'}
              dark={dark}
              height={chipHeight}
              onClick={() => mudarFiltro('sem-matricula')}
            />
            <CountChip
              label="Divergentes"
              count={totais.divergentes}
              active={filtro === 'divergentes'}
              dark={dark}
              height={chipHeight}
              onClick={() => mudarFiltro('divergentes')}
            />

            <div style={{ flex: 1 }} />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                // 44 na moldura para o campo alcançar os 40px de alvo com o
                // `box-sizing: border-box` do globals.css (2px de borda de cada
                // lado). No celular ela ocupa a linha inteira: 220px fixos ao
                // lado de seis chips deixavam a busca encavalada.
                height: byViewport(vp, { mobile: 44, tablet: 44, desktop: 30 }),
                padding: '0 10px',
                background: p.surface,
                border: `1px solid ${p.border}`,
                borderRadius: 6,
                width: byViewport<number | string>(vp, {
                  mobile: '100%',
                  tablet: 240,
                  desktop: 240,
                }),
                fontSize: 11,
                color: p.muted,
              }}
            >
              <IconSearch size={13} />
              <input
                value={busca}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => mudarBusca(e.target.value)}
                placeholder="Nome, e-mail ou matrícula…"
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: 11.5,
                  color: p.text,
                  fontFamily: fontSans,
                  minHeight: byViewport(vp, { mobile: 40, tablet: 40, desktop: 0 }),
                }}
              />
            </div>
          </div>

          {/* Legenda · sem ela a tela mente por omissão: quem vê "Férias" numa
              conta ATIVA acha que é bug, e quem vê a coluna de setor com dois
              valores não sabe qual deles manda no acesso. */}
          <div
            style={{
              fontSize: 11.5,
              lineHeight: 1.6,
              color: p.muted,
              marginBottom: 14,
              maxWidth: 900,
            }}
          >
            O valor de cima em <strong>Setor</strong> e <strong>Filial</strong> é o que está gravado
            na conta — é <strong>ele</strong> que decide o que a pessoa vê. O “RH:” embaixo aparece
            só quando o RH já diz outra coisa. Acesso é liberado apenas para situação{' '}
            <strong>A (ativo)</strong> e <strong>F (férias)</strong> no RH: férias mantém o acesso
            de propósito.
          </div>

          <AdminInvitesPanel dark={dark} accent={accent} controlHeight={chipHeight} />

          <div style={painel}>
            {/* Celular: pilha de cards. Tablet: a mesma grade dentro de um
                rolador lateral com piso de largura. Desktop: a grade nua. */}
            {vp.mobile && vazio}
            {vp.mobile && cards}
            {vp.tablet && (
              <div style={TABLE_SCROLLER}>
                <div style={{ minWidth: TABLE_MIN_WIDTH }}>{grade}</div>
              </div>
            )}
            {vp.desktop && grade}
          </div>

          {/* Paginação — mesmo vocabulário do Inventário: contador à esquerda,
              tamanho de página e Anterior/Próximo à direita. Age sobre a lista
              JÁ filtrada/buscada; trocar filtro ou busca volta à página 1. */}
          {visiveis.length > 0 && (
            <div
              style={{
                ...numeroTabular,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                marginTop: 12,
                fontSize: 12,
                color: p.muted,
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>
                Mostrando {inicio + 1} a {Math.min(inicio + porPagina, visiveis.length)} de{' '}
                {visiveis.length} {visiveis.length === 1 ? 'conta' : 'contas'}
                {visiveis.length !== users.length ? ` · filtradas de ${users.length}` : ''}
              </span>
              <div style={{ flex: 1 }} />
              {/* SelectMenu no lugar do <select>: a lista do nativo é do sistema
                  e destoava do produto. <span>, não <label>: o rótulo acessível
                  já vive no aria-label do combobox. */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>Por página</span>
                <SelectMenu
                  value={porPagina}
                  onChange={(v) => {
                    setPorPagina(v);
                    setPagina(1);
                  }}
                  label="Por página"
                  options={[25, 50, 100].map((n) => ({ value: n, label: String(n) }))}
                  tone={{
                    bg: p.surface,
                    text: p.text,
                    border: p.border,
                    borderActive: `${accent}66`,
                    panelBg: p.surface,
                    panelBorder: p.border,
                    optionHover: p.surface2,
                    optionSelected: accent,
                    muted: p.muted,
                    height: byViewport(vp, { mobile: 40, desktop: 30 }),
                    fontSize: 12,
                    radius: 6,
                    fontFamily: numeroTabular.fontFamily,
                  }}
                />
              </span>
              {(() => {
                const botao = (rotulo: string, alvo: number, desabilitado: boolean) => (
                  <button
                    type="button"
                    disabled={desabilitado}
                    onClick={() => setPagina(alvo)}
                    style={{
                      height: byViewport(vp, { mobile: 40, desktop: 30 }),
                      padding: '0 12px',
                      background: p.surface,
                      color: desabilitado ? p.muted : p.text,
                      border: `1px solid ${p.border}`,
                      borderRadius: 6,
                      fontSize: 12,
                      fontFamily: fontSans,
                      fontWeight: 600,
                      cursor: desabilitado ? 'default' : 'pointer',
                      opacity: desabilitado ? 0.55 : 1,
                    }}
                  >
                    {rotulo}
                  </button>
                );
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {botao('Anterior', paginaAtual - 1, paginaAtual <= 1)}
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {paginaAtual} / {totalPaginas}
                    </span>
                    {botao('Próximo', paginaAtual + 1, paginaAtual >= totalPaginas)}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
